import React, { FC, memo } from 'react';

import classNames from 'classnames';
import Checkbox from '@zvuk-b2b/react-uikit/ui/Checkbox';
import InputSearch, { InputSize } from '@zvuk-b2b/react-uikit/ui/InputSearch';
import Popper, { HintPlacement } from '@zvuk-b2b/react-uikit/ui/Popper';
import IconFilterFilled from '@zvuk-b2b/react-uikit/ui/icons/FilterFilled';
import IconSortAscending from '@zvuk-b2b/react-uikit/ui/icons/SortAscending';
import IconSortDescending from '@zvuk-b2b/react-uikit/ui/icons/SortDescending';

import { PromoClipsQuerySort } from '@shared/api/graphql/generated';

import { DATA_TEST_IDS } from '../../consts';
import { TEXTS } from '../../texts';
import { PROMO_STATUS_OPTIONS } from '../consts';
import type { PromoSelectorSearchRowProps } from './types';

import styles from './index.module.scss';

const PromoSelectorSearchRow: FC<PromoSelectorSearchRowProps> = ({
  filterButtonRef,
  filterContainerRef,
  isFilterOpen,
  search,
  selectedStatuses,
  sort,
  statusesInfo,
  onFilterToggle,
  onSearchChange,
  onSortToggle,
  onStatusChange,
}) => {
  const isNewestFirst = sort === PromoClipsQuerySort.CreatedDesc;
  const sortLabel = isNewestFirst ? TEXTS.AUDIO_REPORT_PROMOS_SORT_LABEL : TEXTS.AUDIO_REPORT_PROMOS_SORT_ASC_LABEL;
  const filterLabel = selectedStatuses.length
    ? `${TEXTS.AUDIO_REPORT_PROMOS_FILTER_LABEL}: ${selectedStatuses.length}`
    : TEXTS.AUDIO_REPORT_PROMOS_FILTER_LABEL;

  return (
    <div
      className={styles.searchRow}
      data-testid={DATA_TEST_IDS.AUDIO_REPORT_PROMOS_TOOLBAR}>
      <div
        className={styles.search}
        data-testid={DATA_TEST_IDS.AUDIO_REPORT_PROMOS_SEARCH_CONTAINER}>
        <InputSearch
          elementId={DATA_TEST_IDS.AUDIO_REPORT_PROMOS_SEARCH}
          value={search}
          placeholder={TEXTS.AUDIO_REPORT_PROMOS_SEARCH_PLACEHOLDER}
          size={InputSize.SMALL}
          autoComplete="off"
          containerClassName={styles.searchInputContainer}
          inputClassName={styles.input}
          onChange={onSearchChange}
        />
      </div>
      <Popper
        elementId={DATA_TEST_IDS.AUDIO_REPORT_PROMOS_SORT_TOOLTIP}
        popperClassName={styles.sortTooltip}
        placement={HintPlacement.PLACEMENT_TOP}
        offset={[4, 4]}
        triggerNode={
          <button
            type="button"
            className={`${styles.control} ${styles.sortControl}`}
            data-testid={DATA_TEST_IDS.AUDIO_REPORT_PROMOS_SORT}
            aria-label={sortLabel}
            aria-pressed={!isNewestFirst}
            onClick={onSortToggle}>
            <span
              className={styles.sortIcon}
              data-testid={DATA_TEST_IDS.AUDIO_REPORT_PROMOS_SORT_ICON}
              aria-hidden="true">
              {isNewestFirst ? <IconSortDescending /> : <IconSortAscending />}
            </span>
          </button>
        }>
        {sortLabel}
      </Popper>
      <div className={styles.filterWrapper}>
        <button
          ref={filterButtonRef}
          type="button"
          className={`${styles.control} ${styles.filterControl}`}
          data-testid={DATA_TEST_IDS.AUDIO_REPORT_PROMOS_FILTER}
          aria-controls={DATA_TEST_IDS.AUDIO_REPORT_PROMOS_STATUS_FILTER}
          aria-expanded={isFilterOpen}
          aria-label={filterLabel}
          title={filterLabel}
          onClick={onFilterToggle}>
          <span
            className={styles.filterIcon}
            data-testid={DATA_TEST_IDS.AUDIO_REPORT_PROMOS_FILTER_ICON}
            aria-hidden="true">
            <IconFilterFilled />
            {selectedStatuses.length > 0 && (
              <span
                className={styles.filterBadge}
                data-testid={DATA_TEST_IDS.AUDIO_REPORT_PROMOS_FILTER_BADGE}
              />
            )}
          </span>
        </button>

        {isFilterOpen && (
          <div
            ref={filterContainerRef}
            id={DATA_TEST_IDS.AUDIO_REPORT_PROMOS_STATUS_FILTER}
            className={styles.filterPopover}
            data-testid={DATA_TEST_IDS.AUDIO_REPORT_PROMOS_STATUS_FILTER}>
            {PROMO_STATUS_OPTIONS.map(({ key, label, status }) => {
              const isChecked = selectedStatuses.includes(status);

              return (
                <Checkbox
                  elementId={DATA_TEST_IDS.AUDIO_REPORT_PROMOS_STATUS_FILTER_OPTION_CHECKBOX(status)}
                  key={status}
                  buttonClassName={styles.filterCheckboxButton}
                  labelClassName={styles.filterCheckboxLabel}
                  isChecked={isChecked}
                  onChange={(isSelected) => onStatusChange(status, isSelected)}>
                  <span
                    className={styles.filterOptionContent}
                    data-testid={DATA_TEST_IDS.AUDIO_REPORT_PROMOS_STATUS_FILTER_OPTION(status)}>
                    <span
                      className={classNames(styles.filterStatusDot, {
                        [styles.filterStatusDotActive]: key === 'active',
                        [styles.filterStatusDotPassed]: key === 'passed',
                        [styles.filterStatusDotPlanned]: key === 'planned',
                        [styles.filterStatusDotStopped]: key === 'stopped',
                      })}
                      data-testid={DATA_TEST_IDS.AUDIO_REPORT_PROMOS_STATUS_FILTER_OPTION_INDICATOR(status)}
                      aria-hidden="true"
                    />
                    <span>{label}</span>
                    <span className={styles.filterStatusCount}>{statusesInfo?.[key] ?? '—'}</span>
                  </span>
                </Checkbox>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(PromoSelectorSearchRow);
